import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { RiMenu3Fill } from 'react-icons/ri';
import { FaXmark } from 'react-icons/fa6';
import { FaAngleDown } from 'react-icons/fa';
import { SiVerizon } from 'react-icons/si';
import { t } from 'i18next';
import './Navbar.css';

// Same items, same paths, same translation keys as the original component.
// (Desktop and mobile keep their original — different — ordering on purpose,
// since that's how the source file had them. Reorder here if that was a bug.)
const NAV_ITEMS_DESKTOP = [
  { path: '/', key: 'Home', labelKey: 'navbarTxt.home' },
  { path: '/product', key: 'product', labelKey: 'navbarTxt.products' },
  { path: '/service', key: 'service', labelKey: 'navbarTxt.service' },
  { path: '/about', key: 'about', labelKey: 'navbarTxt.about' },
  { path: '/Contact', key: 'Contact', labelKey: 'navbarTxt.contact' },
  { path: '/career', key: 'career', labelKey: 'navbarTxt.career' },
];

const NAV_ITEMS_MOBILE = [
  { path: '/', key: 'Home', labelKey: 'navbarTxt.home' },
  { path: '/about', key: 'about', labelKey: 'navbarTxt.about' },
  { path: '/Contact', key: 'Contact', labelKey: 'navbarTxt.contact' },
  { path: '/service', key: 'service', labelKey: 'navbarTxt.service' },
  { path: '/product', key: 'product', labelKey: 'navbarTxt.products' },
  { path: '/career', key: 'career', labelKey: 'navbarTxt.career' },
];

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'عربی' },
  { code: 'kr', label: 'کوردی' },
];

export const Navbar = () => {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const isLatin = language === 'en' || language === 'en-us';
  const dir = isLatin ? 'ltr' : 'rtl';

  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hideOnScroll, setHideOnScroll] = useState(false);
  const [activeNavItem, setActiveNavItem] = useState('');
  const location = useLocation();

  const languageWrapRef = useRef(null);
  const languageBtnRef = useRef(null);
  const hamburgerRef = useRef(null);
  const closeBtnRef = useRef(null);

  // ---- Detect active route (unchanged logic) ----
  useEffect(() => {
    const path = location.pathname;
    const navItem = path === '/' ? 'Home' : path.replace('/', '');
    setActiveNavItem(navItem);
  }, [location]);

  // ---- Scroll behaviour: solid/glass surface after 20px, ----
  // ---- gentle hide-on-scroll-down for mobile (only while menu is closed) ----
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setIsScrolled(y > 20);

        if (!isMenuOpen) {
          const scrollingDown = y > lastY && y > 80;
          setHideOnScroll(scrollingDown);
        } else {
          setHideOnScroll(false);
        }
        lastY = y;
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isMenuOpen]);

  // ---- Close language dropdown on outside click ----
  useEffect(() => {
    if (!languageMenuOpen) return;
    const handleClick = (e) => {
      if (languageWrapRef.current && !languageWrapRef.current.contains(e.target)) {
        setLanguageMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [languageMenuOpen]);

  // ---- Escape closes whichever is open; lock body scroll while mobile menu is open ----
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== 'Escape') return;
      if (languageMenuOpen) {
        setLanguageMenuOpen(false);
        languageBtnRef.current?.focus();
      } else if (isMenuOpen) {
        setIsMenuOpen(false);
        hamburgerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKey);

    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      // Move focus into the panel once it mounts.
      requestAnimationFrame(() => closeBtnRef.current?.focus());
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen, languageMenuOpen]);

  const toggleMenuLanguage = () => setLanguageMenuOpen((v) => !v);
  const toggleMenu = () => setIsMenuOpen((v) => !v);
  const closeMenu = () => {
    setIsMenuOpen(false);
    hamburgerRef.current?.focus();
  };
  const toggleCloseMenuNavigation = () => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const handleLanguageClick = (lng) => {
    i18n.changeLanguage(lng);
    setLanguageMenuOpen(false);
    window.location.pathname = '/';
  };

  // Simple roving keyboard support inside the language listbox.
  const handleLangKeyDown = useCallback((e) => {
    const items = Array.from(
      e.currentTarget.querySelectorAll('[role="option"]')
    );
    const currentIndex = items.indexOf(document.activeElement);

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      const next = (currentIndex + dir + items.length) % items.length;
      items[next]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1]?.focus();
    }
  }, []);

  return (
    <nav
      dir={dir}
      className={[
        'navbar-root fixed inset-x-0 top-0 z-50 border-b transition-[background-color,box-shadow,border-color,padding-block] duration-500 ease-out',
        isScrolled
          ? 'bg-black/80 border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.35)] backdrop-blur-xl'
          : 'bg-gradient-to-r from-black/60 via-black/35 to-black/60 border-white/5 backdrop-blur-sm',
        hideOnScroll ? 'md:translate-y-0 -translate-y-full' : 'translate-y-0',
        'motion-safe:transition-transform motion-safe:duration-300',
        isLatin ? 'font-serif' : 'font-speda',
      ].join(' ')}
    >
      <div
        className={[
          'mx-auto flex max-w-7xl items-center justify-between px-6 transition-[padding-block] duration-500 ease-out md:px-10',
          isScrolled ? 'py-3' : 'py-4 md:py-5',
        ].join(' ')}
      >
        {/* Logo — left exactly as in the source file (no logo markup was provided there) */}
        {/* Logo */}

        {/* Desktop Nav */}
        <ul className="hidden items-center text-lg uppercase tracking-wide text-white md:flex">
          {NAV_ITEMS_DESKTOP.map((item) => {
            const isActive = activeNavItem === item.key;
            return (
              <li key={item.key} className="mx-4">
                <Link
                  to={item.path}
                  onClick={() => window.scrollTo(0, 0)}
                  aria-current={isActive ? 'page' : undefined}
                  className={[
                    'navlink relative pb-1 transition-colors duration-300 hover:text-yellow-400',
                    isActive ? 'text-yellow-400 is-active' : 'text-white',
                  ].join(' ')}
                >
                  {t(item.labelKey)}
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="flex items-center gap-4">
          {/* Language Selector (desktop) */}
          <div ref={languageWrapRef} className="relative hidden select-none text-white md:block">
            <button
              ref={languageBtnRef}
              type="button"
              onClick={toggleMenuLanguage}
              aria-haspopup="listbox"
              aria-expanded={languageMenuOpen}
              aria-controls="language-listbox-desktop"
              aria-label={t('navbarTxt.language') || 'Language'}
              className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-lg uppercase transition-colors duration-200 hover:text-yellow-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-400"
            >
              {language}
              <motion.span
                animate={{ rotate: languageMenuOpen ? 180 : 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="flex"
              >
                <FaAngleDown size={16} />
              </motion.span>
            </button>

            <AnimatePresence>
              {languageMenuOpen && (
                <motion.ul
                  id="language-listbox-desktop"
                  role="listbox"
                  aria-label={t('navbarTxt.language') || 'Language'}
                  onKeyDown={handleLangKeyDown}
                  initial={{ opacity: 0, y: -8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.97 }}
                  transition={{ duration: 0.16, ease: 'easeOut' }}
                  className="absolute end-0 top-full mt-3 w-36 space-y-1 rounded-xl border border-white/15 bg-black/85 p-2 shadow-2xl backdrop-blur-lg lang-menu"
                >
                  {LANGUAGES.map((lng) => {
                    const selected = language === lng.code;
                    return (
                      <li key={lng.code} role="presentation">
                        <button
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => handleLanguageClick(lng.code)}
                          className={[
                            'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-base transition-colors duration-150 hover:bg-white/10 hover:text-yellow-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow-400',
                            selected ? 'text-yellow-400' : 'text-white',
                          ].join(' ')}
                        >
                          <span>{lng.label}</span>
                          {selected && <SiVerizon className="shrink-0" size={14} />}
                        </button>
                      </li>
                    );
                  })}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>

          {/* Mobile Hamburger */}
          <button
            ref={hamburgerRef}
            type="button"
            className="grid h-11 w-11 place-items-center rounded-md text-3xl text-white transition-colors duration-200 hover:text-yellow-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-400 md:hidden"
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu-panel"
            aria-label={isMenuOpen ? (t('navbarTxt.closeMenu') || 'Close menu') : (t('navbarTxt.openMenu') || 'Open menu')}
          >
            <AnimatePresence mode="wait" initial={false}>
              {isMenuOpen ? (
                <motion.span
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="flex"
                >
                  <FaXmark />
                </motion.span>
              ) : (
                <motion.span
                  key="open"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="flex"
                >
                  <RiMenu3Fill />
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* Mobile Menu: overlay + sliding panel */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              key="overlay"
              onClick={closeMenu}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-[998] bg-black/60 backdrop-blur-sm md:hidden"
              aria-hidden="true"
            />

            <motion.div
              id="mobile-menu-panel"
              key="panel"
              role="dialog"
              aria-modal="true"
              aria-label={t('navbarTxt.mobileMenu') || 'Menu'}
              dir={dir}
              initial={{ x: dir === 'rtl' ? '-100%' : '100%' }}
              animate={{ x: 0 }}
              exit={{ x: dir === 'rtl' ? '-100%' : '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 32 }}
              className="fixed top-0 end-0 z-[999] flex h-full w-72 max-w-[85vw] flex-col items-center bg-gray-600 bg-gradient-to-t from-black/80 to-black/90 pt-24 text-xl uppercase text-white shadow-2xl backdrop-blur-2xl"
            >
              <button
                ref={closeBtnRef}
                type="button"
                onClick={closeMenu}
                aria-label={t('navbarTxt.closeMenu') || 'Close menu'}
                className="absolute top-6 end-6 grid h-11 w-11 place-items-center rounded-md text-3xl transition-colors duration-200 hover:text-yellow-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow-400"
              >
                <FaXmark />
              </button>

              <ul className="flex w-full flex-col items-center space-y-6">
                {NAV_ITEMS_MOBILE.map((item) => {
                  const isActive = activeNavItem === item.key;
                  return (
                    <li key={item.key}>
                      <Link
                        to={item.path}
                        onClick={toggleCloseMenuNavigation}
                        aria-current={isActive ? 'page' : undefined}
                        className={[
                          'inline-block rounded-md px-3 py-2 transition-colors duration-200 hover:text-yellow-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow-400',
                          isActive ? 'text-yellow-400' : 'text-white',
                        ].join(' ')}
                      >
                        {t(item.labelKey)}
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {/* Mobile Language Section */}
              <div className="mt-6 flex w-3/4 flex-col items-center justify-center border-t border-yellow-500 pt-6">
                <p className="mb-3 text-center text-yellow-500">{t('navbarTxt.language')}</p>

                <div className="flex flex-col items-center space-y-2 text-lg">
                  {LANGUAGES.map((lng) => (
                    <button
                      key={lng.code}
                      type="button"
                      onClick={() => handleLanguageClick(lng.code)}
                      aria-current={language === lng.code ? 'true' : undefined}
                      className={[
                        'rounded-md px-3 py-1.5 transition-colors duration-200 hover:text-yellow-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow-400',
                        language === lng.code ? 'text-yellow-400' : 'text-white',
                      ].join(' ')}
                    >
                      {lng.label}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};